import React, { useState, useEffect, useRef } from "react";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  required,
  Create,
  SimpleForm,
  TextInput,
  FileInput,
  Toolbar,
  useRedirect,
  useNotify,
  FormDataConsumer,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import { Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@material-ui/core";
import * as S from "./index.styles";
import {
  validateCsvFile,
  generateErrorCsv,
  uploadErrorCsvToS3,
  downloadErrorCsv,
  downloadErrorCsvByDescription,
  downloadTemplateExample,
} from "./validationUtils";

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file.rawFile);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const validateRequired = required();


// Componente para observar mudanças no arquivo
const FileValidator = ({ file, onValidate }) => {
  const lastFileRef = useRef(null);

  useEffect(() => {
    if (file && file.rawFile && lastFileRef.current !== file.rawFile) {
      lastFileRef.current = file.rawFile;
      onValidate(file);
    } else if (!file) {
      lastFileRef.current = null;
      onValidate(null);
    }
  }, [file, onValidate]);

  return null;
};

// Componente para observar mudanças no description
const DescriptionObserver = ({ description, onDescriptionChange }) => {
  useEffect(() => {
    if (description !== undefined) {
      onDescriptionChange(description || "");
    }
  }, [description, onDescriptionChange]);

  return null;
};

const CustomToolbar = ({ csvValidation, onOpenModal, ...props }) => {
  const redirect = useRedirect();
  const { watch } = useFormContext();
  const description = watch("description");
  const file = watch("file");

  // Formulário é válido se: descrição preenchida, arquivo existe e CSV é válido
  const isFormValid =
    description &&
    file &&
    csvValidation &&
    csvValidation.isValid !== false &&
    csvValidation.isValid === true;

  const handleCancel = () => {
    redirect("/consolidated-delivered-orders");
  };

  const handleOpenConfirmModal = () => {
    if (isFormValid && onOpenModal) {
      onOpenModal();
    }
  };

  return (
    <Toolbar {...props} sx={{ display: "flex", justifyContent: "flex-start", gap: "16px", padding: "16px 0", borderTop: "1px solid #e0e0e0" }}>
      <S.ToolbarCancelMuiButton onClick={handleCancel}>
        CANCELAR
      </S.ToolbarCancelMuiButton>
      <S.ToolbarSendMuiButton
        data-send-valid={isFormValid ? "true" : "false"}
        disabled={!isFormValid}
        onClick={handleOpenConfirmModal}
      >
        ENVIAR
      </S.ToolbarSendMuiButton>
    </Toolbar>
  );
};

const ConsolidatedDeliveredOrdersCreate = (props) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [csvValidation, setCsvValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [errorCsv, setErrorCsv] = useState(null);
  const [errorCsvS3Url, setErrorCsvS3Url] = useState(null); // Nova state para URL do S3
  const [errorCsvS3Filename, setErrorCsvS3Filename] = useState(null); // Nome usado no S3 no upload (para download mesmo se a descrição for alterada)
  const [descriptionValue, setDescriptionValue] = useState(""); // Novo estado para armazenar description
  const [errorFileInfo, setErrorFileInfo] = useState(null); // Nome, tamanho e data do arquivo quando há erro
  const [successFileInfo, setSuccessFileInfo] = useState(null); // Nome, tamanho e data quando CSV válido
  const successFileRef = useRef(null); // Referência ao arquivo para download
  const lastFileRef = useRef(null);
  const formRef = useRef(null);
  const notify = useNotify();

  const formatFileSize = (bytes) => {
    if (bytes == null) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatFileDate = (timestamp) => {
    if (timestamp == null) return "-";
    return new Date(timestamp).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const sanitizeFilename = (filename) => {
    if (!filename) return "erros-validacao-csv";
    // Remover caracteres inválidos para nomes de arquivo
    return filename
      .replace(/[^a-zA-Z0-9\s\-_]/g, "") // Remove caracteres especiais
      .replace(/\s+/g, "-") // Substitui espaços por hífens
      .trim() || "erros-validacao-csv"; // Fallback se ficar vazio
  };

  const validateFile = async (file, description = "") => {
    if (!file || !file.rawFile) {
      setCsvValidation(null);
      setErrorCsv(null);
      setErrorCsvS3Url(null);
      setErrorCsvS3Filename(null);
      setErrorFileInfo(null);
      setSuccessFileInfo(null);
      successFileRef.current = null;
      lastFileRef.current = null;
      return;
    }

    const fileName = file.rawFile.name || "";
    if (!fileName.toLowerCase().endsWith(".csv")) {
      setCsvValidation({
        isValid: false,
        error: "Arquivo inválido. Apenas arquivos .CSV são aceitos.",
      });
      lastFileRef.current = null;
      return;
    }

    // Verificar se a descrição está preenchida
    if (!description || description.trim() === "") {
      setCsvValidation({
        isValid: false,
        error: "Por favor, preencha o campo Descrição antes de fazer upload do arquivo.",
      });
      return;
    }

    // Evitar validação duplicada do mesmo arquivo
    if (lastFileRef.current === file.rawFile) {
      return;
    }
    lastFileRef.current = file.rawFile;

    setIsValidating(true);
    setCsvValidation(null);
    setErrorCsv(null);
    setErrorCsvS3Url(null);
    setErrorCsvS3Filename(null);
    setErrorFileInfo(null);
    setSuccessFileInfo(null);
    successFileRef.current = null;

    try {
      const csvText = await readFileAsText(file);
      const originalHeaders = csvText.split("\n")[0]?.split(";") || [];

      const validation = await validateCsvFile(csvText, originalHeaders);

      setCsvValidation(validation);

      if (validation.isValid) {
        setSuccessFileInfo({
          name: file.rawFile.name,
          size: file.rawFile.size,
          lastModified: file.rawFile.lastModified,
        });
        successFileRef.current = file.rawFile;
        notify("CSV válido! Você pode enviar o formulário.", { type: "success" });
      } else {
        // Gerar CSV de erros
        const errorCsvContent = generateErrorCsv(
          csvText,
          validation.validationResults,
          originalHeaders
        );
        setErrorCsv(errorCsvContent);
        setErrorFileInfo({
          name: file.rawFile.name,
          size: file.rawFile.size,
          lastModified: file.rawFile.lastModified,
        });

        const filename = sanitizeFilename(description);
        setErrorCsvS3Filename(filename); // Guardar nome usado no S3 para download mesmo se a descrição for alterada

        // AUTOMATICAMENTE fazer upload do CSV de erros no S3
        setIsUploading(true);
        try {
          const uploadResponse = await uploadErrorCsvToS3(errorCsvContent, filename);
          const s3Url = uploadResponse.s3Url || uploadResponse.url || uploadResponse;
          setErrorCsvS3Url(s3Url);
        } catch (uploadError) {
          console.error("Erro ao fazer upload do CSV de erros:", uploadError);
          notify(uploadError.message || "Erro ao fazer upload do CSV de erros.", { type: "error" });
        } finally {
          setIsUploading(false);
        }
      }
    } catch (error) {
      setCsvValidation({
        isValid: false,
        error: error.message,
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleDownloadErrors = async () => {
    // Preferir download a partir do CSV em memória (não depende da descrição nem do S3)
    if (errorCsv) {
      const name = errorCsvS3Filename || descriptionValue || "erros-validacao";
      downloadErrorCsv(errorCsv, name);
      notify("Download do arquivo de erros iniciado", { type: "success" });
      return;
    }
    // Fallback: baixar do S3 usando o nome que foi usado no upload (não o campo descrição atual)
    const keyForDownload = errorCsvS3Filename || descriptionValue;
    if (!keyForDownload) {
      notify("Por favor, preencha o campo Descrição para fazer o download", { type: "warning" });
      return;
    }
    setIsUploading(true);
    try {
      await downloadErrorCsvByDescription(keyForDownload);
      notify("Download do arquivo de erros iniciado", { type: "success" });
    } catch (error) {
      notify(`Erro ao fazer download: ${error.message}`, { type: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    try {
      await downloadTemplateExample();
      notify("Download do template iniciado", { type: "success" });
    } catch (error) {
      notify(`Erro ao fazer download do template: ${error.message}`, { type: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmSend = () => {
    setIsModalOpen(false);
    // Disparar o submit do formulário
    setTimeout(() => {
      if (formRef.current) {
        const form = formRef.current.querySelector('form');
        if (form) {
          // Criar e disparar evento de submit
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }
      }
    }, 100);
  };

  return (
    <>
      <Create redirect="list" title="" {...props} sx={{ "& .RaCreate-main": { maxWidth: "100%", width: "100%" } }}>
        <div ref={formRef}>
          <SimpleForm
            toolbar={<CustomToolbar csvValidation={csvValidation} onOpenModal={handleOpenModal} />}
            className="consolidated-delivered-orders-create"
            sx={{ maxWidth: "100%", width: "100%" }}
          >
        <S.Container>
          <S.Header>
            <S.Title>Novo Envio de Integração</S.Title>
            <S.Subtitle>
              Preencha os dados e faça upload do arquivo CSV para processar
            </S.Subtitle>
          </S.Header>

          <S.FormContainer>
            <TextInput
              source="description"
              label="Descrição"
              validate={validateRequired}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff",
                },
              }}
            />

            <FormDataConsumer>
              {({ formData }) => (
                <>
                  <DescriptionObserver 
                    description={formData?.description} 
                    onDescriptionChange={setDescriptionValue}
                  />

                  <S.StyledFileInput isValid={csvValidation?.isValid === true}>
                    <FileInput
                      source="file"
                      label={false}
                      accept=".csv,text/csv"
                      validate={validateRequired}
                      disabled={!formData?.description || formData?.description.trim() === ""}
                      className="consolidated-delivered-orders-create"
                      options={{
                        onDropRejected: () => {
                          notify("Arquivo inválido. Apenas arquivos .CSV são aceitos.", { type: "error" });
                        },
                      }}
                      placeholder={
                        !formData?.description || formData?.description.trim() === "" ? (
                          <>
                            <CloudUploadIcon style={{ fontSize: 48, color: '#BDBDBD', marginBottom: 12 }} />
                            <p style={{ margin: '8px 0 4px', fontSize: 14, fontWeight: 500, color: '#9E9E9E' }}>
                              Preencha o campo Descrição para habilitar o upload
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: '#9E9E9E' }}>
                              Formato aceito: .CSV
                            </p>
                          </>
                        ) : isValidating ? (
                          <>
                            <CircularProgress size={32} style={{ marginBottom: 8, color: '#1976D2' }} />
                            <p style={{ margin: '8px 0 4px', fontSize: 18, fontWeight: 500, color: '#333' }}>
                              Processando arquivo...
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: '#999' }}>
                              Aguarde enquanto validamos os dados
                            </p>
                          </>
                        ) : csvValidation?.isValid && formData?.file?.rawFile ? (
                          <>
                            <span className="file-input-success-icon">
                              <CheckCircleIcon style={{ fontSize: 48, color: '#2e7e33', marginBottom: 12 }} />
                            </span>
                            <p style={{ margin: '8px 0 4px', fontSize: 14, fontWeight: 600, color: '#333' }}>
                              {formData.file.rawFile.name}
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: '#999' }}>
                              Formato aceito: .CSV
                            </p>
                          </>
                        ) : (
                          <>
                            <CloudUploadIcon style={{ fontSize: 48, color: '#1976D2', marginBottom: 12 }} />
                            <p style={{ margin: '8px 0 4px', fontSize: 14, fontWeight: 500, color: '#333' }}>
                              Arraste o arquivo CSV aqui ou clique para selecionar
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: '#999' }}>
                              Formato aceito: .CSV
                            </p>
                          </>
                        )
                      }
                    >
                    </FileInput>
                  </S.StyledFileInput>

                  <FileValidator 
                    file={formData?.file} 
                    onValidate={(file) => validateFile(file, formData?.description || "")} 
                  />
                </>
              )}
            </FormDataConsumer>

            {csvValidation && csvValidation.isValid && !isValidating && successFileInfo && (
              <S.FileFeedbackCard>
                <S.FileInfoRow>
                  <S.FileInfoName>{successFileInfo.name}</S.FileInfoName>
                  <S.FileInfoMeta>
                    <span>Tamanho: {formatFileSize(successFileInfo.size)}</span>
                    <span>Modificado: {formatFileDate(successFileInfo.lastModified)}</span>
                  </S.FileInfoMeta>
                </S.FileInfoRow>
              </S.FileFeedbackCard>
            )}

            {csvValidation && !csvValidation.isValid && !csvValidation.error && errorCsv && !isValidating && (
              <S.FileFeedbackCard>
                <S.FileInfoRow>
                  <S.FileInfoName>{errorFileInfo?.name ?? "Arquivo"}</S.FileInfoName>
                  <S.FileInfoMeta>
                    <span>Tamanho: {formatFileSize(errorFileInfo?.size)}</span>
                    <span>Modificado: {formatFileDate(errorFileInfo?.lastModified)}</span>
                  </S.FileInfoMeta>
                </S.FileInfoRow>
                <S.FileFeedbackSeparator />
                <S.FileErrorSection>
                  <div className="file-error-icon">
                    <ErrorOutlineIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="file-error-message">
                    Encontramos <strong>{csvValidation.totalErrors} erros</strong> no arquivo enviado. Baixe a planilha detalhada para visualizar e corrigir.
                  </div>
                  <div className="file-error-button">
                    <Button
                      onClick={handleDownloadErrors}
                      variant="outlined"
                      size="small"
                      disabled={isUploading || (!errorCsvS3Url && !errorCsv)}
                      startIcon={<GetAppIcon />}
                      style={{
                        color: "#C62828",
                        borderColor: "#C62828",
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      {isUploading ? "Processando..." : "Baixar planilha de erros"}
                    </Button>
                  </div>
                </S.FileErrorSection>
              </S.FileFeedbackCard>
            )}

            {csvValidation && csvValidation.error && !isValidating && (
              <S.ErrorBanner>
                <span className="error-icon">⚠</span>
                <div className="error-text">{csvValidation.error}</div>
              </S.ErrorBanner>
            )}
            {!(csvValidation && !csvValidation.isValid && errorCsv && !isValidating) &&
            !(csvValidation && csvValidation.isValid && !isValidating) && (
              <S.InfoBanner>
                 <div className="file-error-icon">
                    <ErrorOutlineIcon style={{ fontSize: 24, color: '#0288d1' }} />
                  </div>
                <div className="info-text">
                  Baixe o{" "}
                  <a
                    href="#"
                    rel="noopener noreferrer"
                    onClick={handleDownloadTemplate}
                  >
                    Template CSV Padrão
                  </a>{" "}
                  para garantir a formatação correta.
                </div>
              </S.InfoBanner>
            )}
          </S.FormContainer>
        </S.Container>
      </SimpleForm>
        </div>
      </Create>

      {/* Modal de Confirmação */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          style={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#424242",
            padding: "24px 24px 16px",
          }}
        >
          Confirmar envio para integração
        </DialogTitle>
        <DialogContent style={{ padding: "0 24px 24px" }}>
          <Typography
            style={{
              fontSize: "14px",
              color: "#424242",
              lineHeight: 1.6,
            }}
          >
            Os registros validados serão processados internamente e agendados para envio automático ao sales farma durante a madrugada
          </Typography>
        </DialogContent>
        <DialogActions
          style={{
            padding: "0 24px 24px",
            gap: "12px",
            justifyContent: "flex-end",
            display: "flex",
            flexWrap: "wrap",
          }}
        >
          <Button
            onClick={handleCloseModal}
            style={{
              color: "#1976D2",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              padding: "8px 24px",
            }}
          >
            CANCELAR
          </Button>
          <Button
            onClick={handleConfirmSend}
            variant="contained"
            style={{
              backgroundColor: "#1976D2",
              color: "#fff",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              padding: "8px 36px",
              boxShadow: "none",
            }}
          >
            CONFIRMAR ENVIO
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConsolidatedDeliveredOrdersCreate;
